import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Share2, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Footer() {
  const [email, setEmail] = useState('');

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      toast.success('Thank you for subscribing!');
      setEmail('');
    }
  };

  return (
    <footer className="bg-[#0f0f0f] text-gray-400 mt-20">
      {/* Top divider accent */}
      <div className="h-0.5 bg-gradient-to-r from-transparent via-blue-700 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">

          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-[13px] font-black">BC</span>
              </div>
              <div>
                <span className="block text-[18px] font-black tracking-[0.05em] text-white">BabyCub</span>
                <span className="block text-[7px] font-semibold tracking-[0.25em] text-blue-400 uppercase -mt-0.5">Baby Wear</span>
              </div>
            </Link>
            <p className="text-sm leading-relaxed mb-6 text-gray-500">
              Soft, stylish and comfortable baby garments for your little ones.
            </p>
            <div className="space-y-2.5 text-sm">
              <a href="tel:03363898930" className="flex items-center gap-2.5 hover:text-white transition-colors group">
                <Phone size={14} className="text-gray-600 group-hover:text-white transition-colors flex-shrink-0" />
                03363898930
              </a>
              <a href="mailto:babycub@gmail.com" className="flex items-center gap-2.5 hover:text-white transition-colors group">
                <Mail size={14} className="text-gray-600 group-hover:text-white transition-colors flex-shrink-0" />
                babycub@gmail.com
              </a>
              <a href="https://wa.me/923363898930" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 hover:text-white transition-colors group">
                <Share2 size={14} className="text-gray-600 group-hover:text-white transition-colors flex-shrink-0" />
                WhatsApp: 03363898930
              </a>
              <div className="flex items-start gap-2.5">
                <MapPin size={14} className="text-gray-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Street No. 3, Babar Colony, Abu Dhabi Road, Rahim Yar Khan, Punjab, Pakistan</span>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <a href="#" aria-label="Facebook" className="w-9 h-9 rounded-lg border border-gray-700 flex items-center justify-center hover:border-gray-400 hover:text-white transition-all hover:bg-gray-800">
                <Share2 size={16} />
              </a>
              <a href="#" aria-label="Instagram" className="w-9 h-9 rounded-lg border border-gray-700 flex items-center justify-center hover:border-gray-400 hover:text-white transition-all hover:bg-gray-800">
                <Share2 size={16} />
              </a>
              <a href="https://wa.me/923363898930" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg border border-gray-700 flex items-center justify-center hover:border-green-500 hover:text-green-400 transition-all hover:bg-gray-800">
                <Phone size={16} />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h3 className="text-white text-xs font-semibold tracking-widest uppercase mb-5">Shop</h3>
            <ul className="space-y-2.5">
              {[
                { to: '/new-in', label: 'New Arrivals' },
                { to: '/category/baby-boys', label: 'Baby Boys' },
                { to: '/category/baby-girls', label: 'Baby Girls' },
                { to: '/category/rompers', label: 'Rompers' },
                { to: '/category/nightwear', label: 'Nightwear' },
                { to: '/search', label: 'All Products' },
              ].map(item => (
                <li key={item.to}>
                  <Link to={item.to} className="text-sm hover:text-white transition-colors hover:translate-x-0.5 inline-block">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Help */}
          <div>
            <h3 className="text-white text-xs font-semibold tracking-widest uppercase mb-5">Help</h3>
            <ul className="space-y-2.5">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/account', label: 'My Orders' },
                { to: '/refund-policy', label: 'Return Policy (7 Days)' },
                { to: '/refund-policy', label: 'Exchange Policy (14 Days)' },
                { to: '/shipping-policy', label: 'Shipping Policy' },
                { to: '/privacy-policy', label: 'Privacy Policy' },
              ].map(item => (
                <li key={item.label}>
                  <Link to={item.to} className="text-sm hover:text-white transition-colors hover:translate-x-0.5 inline-block">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact / Newsletter */}
          <div>
            <h3 className="text-white text-xs font-semibold tracking-widest uppercase mb-5">Stay Connected</h3>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              For order details, return, exchange or product information, contact BabyCub support.
            </p>
            <a href="tel:03363898930" className="flex items-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg text-sm font-semibold transition-colors mb-3">
              <Phone size={15} /> Call / WhatsApp: 03363898930
            </a>
            <form onSubmit={handleNewsletter}>
              <div className="flex flex-col gap-2 mt-4">
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Newsletter</p>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full bg-gray-800/80 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 rounded-sm transition-colors"
                />
                <button
                  type="submit"
                  className="w-full bg-white text-gray-900 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 rounded-sm"
                >
                  Subscribe <ArrowRight size={13} />
                </button>
              </div>
            </form>
            <div className="mt-5 pt-4 border-t border-gray-800">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="px-2 py-1 border border-gray-700 rounded font-medium">COD</span>
                <span className="px-2 py-1 border border-gray-700 rounded font-medium">7-Day Return</span>
                <span className="px-2 py-1 border border-gray-700 rounded font-medium">14-Day Exchange</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800/80 py-5 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-600">© {new Date().getFullYear()} BabyCub. All Rights Reserved.</p>
          <p className="text-xs text-gray-700">7-Day Return &nbsp;|&nbsp; 14-Day Exchange &nbsp;|&nbsp; COD Available</p>
        </div>
      </div>
    </footer>
  );
}
